// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { FilePreviewByIdModal } from '@dealstack/frontend/admin/pages/common-ui/FilePreviewIdModal';
import { ManageInvestmentWorkflowModal } from '@dealstack/frontend/admin/pages/Manage/User/ManageInvestor/ManageInvestmentWorkflow/ManageInvestmentWorkflowModal';
import { GODMODE_ROOT_ROUTES } from '@dealstack/frontend/admin/pages/Routing/Routing';
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@dealstack/frontend/shared/components/Alert/Alert';
import { Box } from '@dealstack/frontend/shared/components/Box/Box';
import { ConfirmationModal } from '@dealstack/frontend/shared/components/ConfirmationModal/ConfirmationModal';
import { Expandable } from '@dealstack/frontend/shared/components/Expandable/Expandable';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';

import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { GenericBooleanStatus } from '@dealstack/frontend/shared/components/GenericStatus/variants/GenericBooleanStatus/GenericBooleanStatus';
import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import { PhoneNumber } from '@dealstack/frontend/shared/components/PhoneNumber/PhoneNumber';
import { TagPair, TagText } from '@dealstack/frontend/shared/components/TagPair/TagPair';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';
import { deleteKycFile } from '@dealstack/frontend/shared/dataExchange/services/god.service';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { formatBirthday } from '@dealstack/shared/lib/format/dates/date';
import { ApplicationType, GetInvestorAddress } from '@dealstack/shared/lib/generated';
import { useMutation } from '@tanstack/react-query';
import { memo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { useGlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { ReassignInvestorModal } from './ReassignInvestorModal/ReassignInvestorModal';
import { TransactionsActions } from './TransactionsActions';
import { useManageInvestor } from './useManageInvestor';

interface Props {
	className?: string;
}

const ManageInvestor = ({ className }: Props) => {
	const manageInvestorController = useManageInvestor();

	const deleteKycFileRequest = useMutation({
		mutationFn: deleteKycFile,
	});

	const [reassignedInvestorId, setReassignedInvestorId] = useState<null | string>(null);
	const globalConfig = useGlobalConfig();
	const renderInvestorAddress = (address: GetInvestorAddress) => {
		if (address && Object.keys(address)?.length) {
			return `${address.houseNumber} ${address.street} ${address.postCode}, ${address.city}, ${address.country}`.trim();
		}
		return '';
	};

	const renderPhoneNumber = (telephonePrefix: string, phoneNumber: string) => {
		if (telephonePrefix && phoneNumber) {
			return <PhoneNumber telephonePrefix={telephonePrefix} phoneNumber={phoneNumber} />;
		}
		return '';
	};

	if (manageInvestorController?.getInvestorDataRequest?.isLoading) {
		return <FullscreenLoader name="pageLoading" />;
	}

	const user = manageInvestorController?.getInvestorDataRequest?.data?.payload?.user;

	const onDeleteKycFile = (kycFileId: string) => {
		deleteKycFileRequest.mutate(
			{ kycFileId },
			{
				onSuccess: () => {
					showToastV2({ title: 'Deleted successfully' });
					void manageInvestorController.getInvestorDataRequest.refetch();
				},
			}
		);
	};

	return (
		<div className={className}>
			{reassignedInvestorId ? (
				<ReassignInvestorModal
					investorId={reassignedInvestorId}
					onClose={() => {
						setReassignedInvestorId(null);
					}}
				/>
			) : null}
			<Link to={GODMODE_ROOT_ROUTES.MANAGE_USER}>
				<Button variant="project">Back to list</Button>
			</Link>
			<br />
			<TextV2 variant="sans/25/600">User</TextV2>
			<GridBox>
				<TagPair title="Id">{user?.id}</TagPair>
				<TagPair title="First name">{user?.firstName}</TagPair>
				<TagPair title="Last name">{user?.lastName}</TagPair>
				<TagPair title="Email">{user?.email}</TagPair>
				<TagPair title="Is deleted">
					<GenericBooleanStatus value={user?.deleted} />
				</TagPair>
				<Link to={`/manage/user/user/${user?.id}`}>
					<Button variant="secondary">Manage User</Button>
				</Link>
			</GridBox>
			<br />
			<TextV2 variant="sans/17/600">Investor list</TextV2>
			<br />
			<br />
			{user?.investors > 1 && (
				<>
					<Alert icon="information-filled" state="warning" size="m">
						<AlertTitle>WARNING</AlertTitle>
						<AlertDescription>
							Each user have corresponding Investor per Investment Program (MIP). This
							user have invested in multiple MIPs (therefore it have list of Investor
							objects), MAKE SURE TO EDIT CORRECT ONE.
						</AlertDescription>
					</Alert>
					<br />
				</>
			)}
			<ul>
				{user?.investors?.map((investor, index) => {
					const disableRemoveInvestorButton =
						manageInvestorController.removeInvestorFromMipRequest.isLoading ||
						investor?.investmentWorkflows?.some(
							(investmentWorkflow) => investmentWorkflow.isCompleted
						);

					return (
						<InvestmentBlock
							title={`No. ${index + 1} - Mip name: ${investor?.mip?.entity?.displayName}`}
							key={investor?.id}
							preventRenderWhenHidden
						>
							<Box>
								<TextV2 variant="sans/17/600">Investor no: {index + 1}</TextV2>
								<br />
								<br />
								<GridBox>
									<TagPair title="MIP name">
										<TagText>{investor?.mip?.entity?.displayName}</TagText>
									</TagPair>
									<TagPair title="Investor id">
										<TagText>{investor?.id}</TagText>
									</TagPair>
									<TagPair title="State">
										<TagText>{investor?.state}</TagText>
									</TagPair>
									<TagPair title="Deleted">
										<GenericBooleanStatus value={investor?.deleted} />
									</TagPair>
									<TagPair title="Birth date">
										<TagText>{formatBirthday(user?.birthDate)}</TagText>
									</TagPair>
									<GridBox>
										<Link to={`/manage/mip/mip/${investor?.mip?.id}`}>
											<Button variant="secondary">Manage IP (MIP)</Button>
										</Link>
										<Button
											onClick={() => {
												setReassignedInvestorId(investor.id);
											}}
											variant="secondary"
										>
											Reassign this Investor to other User
										</Button>
									</GridBox>
									<ActionButtonWrapper>
										<Link
											to={`../investor/personal-details/${investor.userId}`}
										>
											<UpdateInvestorDetailsButton variant="secondary">
												Update investor personal details
											</UpdateInvestorDetailsButton>
										</Link>

										<RemoveInvestorButton
											variant="delete"
											disabled={disableRemoveInvestorButton}
											onClick={() => {
												manageInvestorController.setInvestorIdToRemove(
													investor.id
												);
											}}
										>
											Remove investor from mip
										</RemoveInvestorButton>
									</ActionButtonWrapper>
								</GridBox>

								<GridBox>
									<TagPair title="Phone number">
										<TagText>
											{renderPhoneNumber(
												user?.telephonePrefix,
												user?.phoneNumber
											)}
										</TagText>
									</TagPair>
									<TagPair title="Address">
										<TagText>{renderInvestorAddress(user?.address)}</TagText>
									</TagPair>
								</GridBox>

								<br />
								<Expandable initialOpened title="Transactions (InvestmentWorkflow)">
									{investor?.investmentWorkflows?.map((investmentWorkflow) => {
										const insertedTransaction =
											manageInvestorController.alreadyInsertedToTransactions?.find(
												(insertedTransaction) =>
													insertedTransaction.pgTsaId ===
													investmentWorkflow.id
											);

										return (
											<InvestmentWorkflowSection key={investmentWorkflow?.id}>
												<TransactionActionsWrapper>
													{!investmentWorkflow?.isCompleted && (
														<ManageInvestmentWorkflowModal
															investmentWorkflowId={
																investmentWorkflow?.id
															}
														/>
													)}

													<TransactionsActions
														investmentWorkflow={investmentWorkflow}
														insertedTransaction={insertedTransaction}
													/>
												</TransactionActionsWrapper>
												<br />
												<GridBox>
													<TagPair title="Id">
														<TagText>{investmentWorkflow?.id}</TagText>
													</TagPair>
													<TagPair title="Transaction Id">
														<TagText>
															{insertedTransaction?._id}
														</TagText>
													</TagPair>
													<TagPair title="Completion step">
														<TagText>
															{investmentWorkflow?.completionStep}
														</TagText>
													</TagPair>
													<TagPair title="Is completed">
														<GenericBooleanStatus
															value={investmentWorkflow?.isCompleted}
														/>
													</TagPair>
													<TagPair title="Payment status">
														<TagText>
															{investmentWorkflow?.paymentStatus}
														</TagText>
													</TagPair>
												</GridBox>

												<br />
											</InvestmentWorkflowSection>
										);
									})}
								</Expandable>

								<br />
								<Expandable title="KYC Files">
									<h2 className="text-2xl font-bold">KYC Files</h2>
									{investor?.kycFiles?.map((kycFile) => {
										return (
											<li key={kycFile?.id}>
												<GridBox>
													<TagPair title="id">
														<TagText>{kycFile?.id}</TagText>
													</TagPair>
													<TagPair title="deleted">
														<GenericBooleanStatus
															value={kycFile?.deleted}
														/>
													</TagPair>
													<TagPair title="status">
														<TagText>
															{kycFile?.status ?? LONG_DASH}
														</TagText>
													</TagPair>
													<TagPair title="type">
														<TagText>{kycFile?.type}</TagText>
													</TagPair>
													<TagPair title="created">
														<TagText>{kycFile?.createdAt}</TagText>
													</TagPair>
													<TagPair title="updated">
														<TagText>{kycFile?.updatedAt}</TagText>
													</TagPair>
													<TagPair title="File">
														<FilePreviewByIdModal
															fileId={kycFile?.fileId}
															buttonText="manage"
														/>
													</TagPair>

													{!kycFile?.deleted && (
														<Button
															variant="delete"
															onClick={() => {
																onDeleteKycFile(kycFile?.id);
															}}
															disabled={
																deleteKycFileRequest.isLoading
															}
														>
															Delete
														</Button>
													)}
												</GridBox>
											</li>
										);
									})}
								</Expandable>

								<br />
								<Expandable title="Investor Invitations">
									{investor?.investmentInvitations?.map(
										(investmentInvitation) => {
											const invitationQuery = new URLSearchParams({
												applicationType: ApplicationType.INVESTOR,
												screenHint: 'sign-up',
												invitationToken: investmentInvitation?.token ?? '',
											});
											const invitationLink = `${globalConfig.apiUrl}/auth/start?${invitationQuery}`;

											return (
												<li key={investmentInvitation?.id}>
													<GridBox>
														<TagPair title="id">
															<TagText>
																{investmentInvitation?.id}
															</TagText>
														</TagPair>
														<TagPair title="used">
															<GenericBooleanStatus
																value={investmentInvitation?.used}
															/>
														</TagPair>
														<TagPair title="token">
															<TagText>
																{investmentInvitation?.token}
															</TagText>
														</TagPair>
														<TagPair title="Link">
															{investmentInvitation?.used ? (
																LONG_DASH
															) : (
																<InvitationLink variant="overline">
																	{invitationLink}
																</InvitationLink>
															)}
														</TagPair>
													</GridBox>
												</li>
											);
										}
									)}
								</Expandable>

								<br />
								{investor?.documents?.length > 0 && (
									<Expandable className={className} title="SDW documents list">
										<ManageSdwDocumentsTable className="table">
											<thead>
												<th>Document id</th>
												<th>Is submitted</th>
												<th>File name</th>
												<th>Action</th>
											</thead>
											<tbody>
												{investor?.documents?.map((document) => {
													return (
														<tr key={document?.id}>
															<td>{document?.id}</td>
															<td>
																{document?.isSubmitted
																	? 'Yes'
																	: 'No'}
															</td>
															<td>{document?.submittedFile?.name}</td>
															<td>
																<FilePreviewByIdModal
																	signatureType={
																		document?.signatureType
																	}
																	overridable
																	fileId={
																		document?.submittedFileId
																	}
																	buttonText="View or replace"
																/>
															</td>
														</tr>
													);
												})}
											</tbody>
										</ManageSdwDocumentsTable>
									</Expandable>
								)}
							</Box>
						</InvestmentBlock>
					);
				})}
			</ul>

			{!!manageInvestorController.investorIdToRemove && (
				<ConfirmationModal
					title="Remove investor from MIP"
					subtitle="This action cannot be undone, are you sure you want to remove investor from MIP?"
					confirmationButtonText="Remove investor"
					onClose={() => {
						manageInvestorController.setInvestorIdToRemove(null);
					}}
					onSubmit={() => {
						manageInvestorController.removeInvestorFromMipRequest.mutate({
							investorId: manageInvestorController.investorIdToRemove,
						});
					}}
				/>
			)}
		</div>
	);
};

const ManageSdwDocumentsTable = styled.table`
	width: 100%;
`;

const InvestmentBlock = styled(Expandable)`
	margin-bottom: 2em;
`;
const ActionButtonWrapper = styled.div`
	display: flex;
	margin-left: auto;
	gap: 1rem;
	margin-right: 0;
`;
const UpdateInvestorDetailsButton = styled(Button)``;
const RemoveInvestorButton = styled(Button)``;
const TransactionActionsWrapper = styled.div`
	display: flex;
	gap: 1rem;
`;

const InvestmentWorkflowSection = styled.li`
	margin-bottom: 2rem;
	background-color: ${({ theme }) => theme.color.white[500]} !important;
	padding: 1em;
	border-radius: 0.5em;

	:not(:last-child) {
		border-bottom: 2px solid ${({ theme }) => theme.color.greys[200]};
	}
`;

const InvitationLink = styled(Text)`
	max-width: 10rem;
`;

const SteppingUl = styled(Box)<{ step?: number }>`
	border: 1px solid #0000001c;
	padding: 0.5rem;
	${({ step }) => (step ? `padding-left: ${step * 3}rem;` : '')}
`;

const GridBox = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 1rem;
	margin-bottom: 2rem;
	overflow: auto;

	& > * {
		margin-right: 2rem;
	}
`;

const Memoized = memo(ManageInvestor);
const Styled = styled(Memoized)`
	${SteppingUl} {
		margin-top: 2rem;
	}

	${TagPair} {
		justify-content: center;
	}
`;

export { Styled as ManageInvestor };
